import {ASSET_FIELD_KEYS, findAssetTypeDescriptor} from '@sollapay/domain/asset-types';

import type {AssetFieldKey} from '@sollapay/domain/asset-types';
import type {AssetAttachmentType, AssetType} from '@sollapay/enums';
import type {AssetDTO, UpsertTrustAssetPayload} from '@sollapay/types';

export interface AssetAttachmentFormValues {
  type: AssetAttachmentType;
  description: string;
}

export interface AssetDetailsFormValues {
  assetType: AssetType | '';
  buildingNumber: string;
  unitNumber: string;
  address: string;
  floor: string;
  block: string;
  parcel: string;
  subParcel: string;
  area: string;
  referencePriceNis: string;
  description: string;
  attachments: AssetAttachmentFormValues[];
}

export const EMPTY_ASSET_DETAILS_FORM_VALUES: AssetDetailsFormValues = {
  assetType: '',
  buildingNumber: '',
  unitNumber: '',
  address: '',
  floor: '',
  block: '',
  parcel: '',
  subParcel: '',
  area: '',
  referencePriceNis: '',
  description: '',
  attachments: []
};

export const toAssetDetailsFormValues = (
  asset: AssetDTO | null | undefined,
  defaultAssetType: AssetType | null = null
): AssetDetailsFormValues => {
  if (!asset) return {...EMPTY_ASSET_DETAILS_FORM_VALUES, assetType: defaultAssetType ?? ''};

  return {
    assetType: asset.assetType,
    buildingNumber: asset.buildingNumber ?? '',
    unitNumber: asset.unitNumber ?? '',
    address: asset.address ?? '',
    floor: asset.floor ?? '',
    block: asset.block ?? '',
    parcel: asset.parcel ?? '',
    subParcel: asset.subParcel ?? '',
    area: asset.area ?? '',
    referencePriceNis: asset.referencePriceNis ?? '',
    description: asset.description ?? '',
    attachments: asset.attachments.map(a => ({type: a.type, description: a.description ?? ''}))
  };
};

/**
 * The attribute keys this asset type accepts — the rule the server enforces in
 * `AssetPolicy.assertValidAttributes`. Resolved through `findAssetTypeDescriptor` rather than
 * `getAssetFieldKeys` so an unset type (an unsaved draft) or one a stale bundle doesn't recognize
 * keeps every field and leaves validation to the server.
 */
const acceptedAttributes = (assetType: AssetType | ''): readonly AssetFieldKey[] =>
  (assetType && findAssetTypeDescriptor(assetType)?.fields.map(field => field.key)) ||
  ASSET_FIELD_KEYS;

/**
 * Whether an extracted field may be applied for this asset type. One extractor serves every asset
 * type, so it can return a field the selected type doesn't have — a land parcel has no
 * `description`. Targets outside the descriptor's attribute set, like `referencePriceNis` (a
 * column of its own), always apply.
 */
export const acceptsAssetField = (assetType: AssetType | '', field: string): boolean =>
  !ASSET_FIELD_KEYS.includes(field as AssetFieldKey) ||
  acceptedAttributes(assetType).includes(field as AssetFieldKey);

export const toUpsertTrustAssetPayload = (
  values: AssetDetailsFormValues
): UpsertTrustAssetPayload => {
  // The form holds every attribute regardless of type; the server rejects any the type doesn't
  // accept, so drop them here rather than send them.
  const accepted = new Set<AssetFieldKey>(acceptedAttributes(values.assetType));
  const attribute = (key: AssetFieldKey): string | null =>
    accepted.has(key) ? values[key].trim() || null : null;

  return {
    assetType: values.assetType as AssetType,
    buildingNumber: attribute('buildingNumber'),
    unitNumber: attribute('unitNumber'),
    address: attribute('address'),
    floor: attribute('floor'),
    block: attribute('block'),
    parcel: attribute('parcel'),
    subParcel: attribute('subParcel'),
    area: attribute('area'),
    description: attribute('description'),
    referencePriceNis: values.referencePriceNis.trim() || null,
    attachments: values.attachments.map(a => ({type: a.type, description: a.description.trim()}))
  };
};
